import type { IntentId } from "../ids";

/**
 * Icone a tratto della richiesta: griglia 24×24, `currentColor`, capi e giunti
 * arrotondati, sempre decorative (`aria-hidden`). Tratto 1,5 per le illustrazioni
 * degli argomenti (lo stesso segno geometrico del pattern della home), 2 per i
 * glifi dell'interfaccia. I simboli ✓ → ● non stanno nel sottoinsieme dei font:
 * qui sono sempre SVG.
 */

const INTENT_PATHS: Record<IntentId, { d: string; circles?: Array<[number, number, number]> }> = {
  offer: {
    d: "M4 10.5h16V20H4z M3 7.5h18v3H3z M12 7.5V20 M12 7.5C10.8 4.9 7.5 4.6 7.5 6.3c0 1.2 2.4 1.2 4.5 1.2 M12 7.5c1.2-2.6 4.5-2.9 4.5-1.2 0 1.2-2.4 1.2-4.5 1.2",
  },
  custom: { d: "M3 13.5h18V21H3z M8 13.5V8.6l1.2-1.8V4h1.6v2.8L12 8.6v4.9 M15.5 13.5l2-1.8-2-1.8 2-1.8-2-1.8" },
  branding: {
    d: "M3.5 12.6 12.6 3.5h7.9v7.9l-9.1 9.1a1.4 1.4 0 0 1-2 0l-5.9-5.9a1.4 1.4 0 0 1 0-2z M12.5 11.8A2.5 2.5 0 1 0 13.1 13.4H11",
    circles: [[16.3, 7.7, 1.5]],
  },
  advice: {
    d: "M12 6.8l2.1 5.2L12 17.2 9.9 12z M12 3.5v1.2M12 19.3v1.2M3.5 12h1.2M19.3 12h1.2",
    circles: [[12, 12, 8.5]],
  },
  order: { d: "M12 3 4 7v10l8 4 8-4V7z M4 7l8 4 8-4 M12 11v10 M8 5l8 4" },
  question: { d: "M3 10.5h11.5V20H3z M3 10.5l1.7-3.3h8.1l1.7 3.3 M19.6 18.6 21.5 20.5", circles: [[17.5, 15.5, 3]] },
  other: { d: "M4 19l1.1-4.2L16 3.9a1.5 1.5 0 0 1 2.1 0l1 1a1.5 1.5 0 0 1 0 2.1L8.2 17.9z M14.4 5.5l3.1 3.1 M13 21.5c1.2-1 2.3-1 3.5 0s2.3 1 3.5 0" },
};

export function IntentIcon({ id, size = 28 }: { id: IntentId; size?: number }) {
  const p = INTENT_PATHS[id];
  return (
    <svg className="rq-tile__icon" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d={p.d} />
      {p.circles?.map(([cx, cy, r]) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />)}
    </svg>
  );
}

type GlyphName = "check" | "next" | "prev" | "plus" | "minus" | "info" | "alert" | "close" | "pen" | "retry";

const GLYPHS: Record<GlyphName, string> = {
  check: "M5 12.5l4.2 4.2L19 7",
  next: "M5 12h14M13 6l6 6-6 6",
  prev: "M19 12H5M11 6l-6 6 6 6",
  plus: "M12 5v14M5 12h14",
  minus: "M5 12h14",
  info: "M12 11v5.5M12 7.6h.01",
  alert: "M12 7v6.5M12 16.6h.01",
  close: "M6 6l12 12M18 6 6 18",
  pen: "M4 19l1.1-4.2L16 3.9a1.5 1.5 0 0 1 2.1 0l1 1a1.5 1.5 0 0 1 0 2.1L8.2 17.9z M13 21.5c1.2-1 2.3-1 3.5 0s2.3 1 3.5 0",
  retry: "M4 12a8 8 0 0 1 13.7-5.6M20 12a8 8 0 0 1-13.7 5.6M17.7 3.2v3.2h-3.2M6.3 20.8v-3.2h3.2",
};

export function Glyph({ name, size = 14, className }: { name: GlyphName; size?: number; className?: string }) {
  const ring = name === "info" || name === "alert";
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={name === "check" ? 2.5 : 2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {ring && <circle cx="12" cy="12" r="9" />}
      <path d={GLYPHS[name]} />
    </svg>
  );
}
